import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import { 
  Shield, LayoutDashboard, ShieldAlert, ScrollText, Users, 
  Settings, LogOut, Activity, Server, Wifi, AlertTriangle, 
  ShieldCheck, UserPlus, ArrowLeft, CreditCard, Copy, Check, BrainCircuit  
} from 'lucide-react';
import SubscriptionGuard from './SubscriptionGuard'; 
import logo from '../img/image-2026-05-21-08-52-40-636.png';

const InstallPage = ({ userRole, onLogout }) => {
  const navigate = useNavigate();
  const [version, setVersion] = useState('py3');
  const [hostname, setHostname] = useState('');
  const [copiedId, setCopiedId] = useState(null); 
  const [stats, setStats] = useState({ 
    subscription_status: 'loading',
    agents_online: 0, 
    uptime: '0h 0m', 
    plan_name: 'FREE', 
    max_servers: 3 
  });
  
  const tenantId = localStorage.getItem('tenant_id') || '';
  const token = localStorage.getItem('token') || '';

  const cleanHostname = hostname.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
  const agentId = cleanHostname ? `srv_${cleanHostname}` : 'srv_your_hostname';
  const displayHost = cleanHostname || 'your-hostname';
  const agentUrl = `https://api-noc.skynix.co/api/agent/source/${version}`;

  useEffect(() => {
    if (!token) return;
    fetch('https://ai-noc.skynix.co/api/stats', {
      headers: { 'X-NOC-Token': token }
    })
    .then(res => res.json())
    .then(data => setStats(data)) 
    .catch(err => console.error('Fetch error:', err));
  }, [token]);

  
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const isLimitReached = stats.agents_online >= stats.max_servers;

  
  const step2Code = `# 1. Download agent script with authorization
wget --no-check-certificate --header="X-NOC-Token: ${token}" ${agentUrl} -O agent.py

# 2. Setup Host Identity
sed -i 's/HOSTNAME = .*/HOSTNAME = "${displayHost}"/' agent.py
sed -i 's/AGENT_ID = .*/AGENT_ID = "${agentId}"/' agent.py

# 3. Apply Enrollment Token
cat <<EOF > ~/.noc_conf
NOC_TOKEN=${token}
EOF

# 4. Set secure permissions
chmod 600 ~/.noc_conf`;

  const startCommand = `nohup ${version === 'py3' ? 'python3' : 'python'} agent.py > agent.log 2>&1 &`;

  return (
    <div className="d-flex" style={{ minHeight: '100vh', backgroundColor: '#0b0e14', color: '#e2e8f0' }}>
       <SubscriptionGuard data={stats} onLogout={onLogout} tenantId={tenantId}>
      
      
      <div className="sidebar" style={{ width: '64px', background: '#0d1117', borderRight: '1px solid #1e293b', height: '100vh', position: 'fixed', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '25px 0', zIndex: 1050 }}>
              <div
                        className="sidebar-item d-flex justify-content-center"
                        data-label="Go to Landing Page" 
                        onClick={() => window.location.href = 'https://noc.skynix.co'}
                        style={{
                          transform: 'translateY(-6px)',
                          marginBottom: '20px',
                          cursor: 'pointer' 
                        }}
                      >
                        <img
                          src={logo}
                          alt="SKYNIX"
                          style={{
                            width: '34px',
                            height: '34px',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
        <div className="sidebar-item" data-label="Dashboard" onClick={() => navigate('/')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><LayoutDashboard size={24} /></div>
        <div className="sidebar-item" data-label="Security Incidents" onClick={() => navigate('/incidents')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><ShieldAlert size={24} /></div>
        <div className="sidebar-item" data-label="System Logs" onClick={() => navigate('/logs')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><ScrollText size={24} /></div>
        <div className="sidebar-item active" data-label="Infrastructure" style={{ background: 'rgba(0, 242, 255, 0.1)', color: '#00f2ff', padding: '10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '30px' }}><Server size={24} /></div>
        
        <div className="sidebar-item" data-label="User Management" onClick={() => navigate('/users')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><Users size={24} /></div>
       
        <div className="sidebar-item" data-label="Billing & Plans"  onClick={() => navigate('/subscription')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><CreditCard size={24} /></div>
        <div className="sidebar-item mt-auto mb-3" data-label="Settings" onClick={() => navigate('/settings')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '20px' }}><Settings size={24} /></div>
        <div className="sidebar-item mb-3" data-label="Logout" onClick={onLogout} style={{cursor: 'pointer'}}><LogOut size={24} className="text-danger" /></div>
      </div>

      <div className="main-wrapper" style={{ marginLeft: '64px', width: 'calc(100% - 64px)' }}>
              
              
              <header className="soc-header" style={{ height: '60px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', background: '#0b0e14' }}>
                <div className="d-flex align-items-center gap-3">
                 
                  <h5 
                  className="sidebar-item tooltip-bottom m-0 d-flex align-items-center"
                  data-label="Go to Landing Page"
                  onClick={() => window.location.href = 'https://noc.skynix.co'}
                  style={{ 
                    color: '#00f2ff', 
                    fontSize: '1.1rem', 
                    cursor: 'pointer',
                    lineHeight: '1',      
                    transform: 'translateY(1px)' 
                  }}
                  >
                    SKYNIX <span className="text-white ms-2">AI-NOC</span>
                  </h5>
                  <div style={{ width: '1px', height: '24px', background: '#334155' }}></div>
                  <div className="text-secondary small d-flex align-items-center gap-2" style={{ fontSize: '0.9rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Activity size={16} className="text-info" /> Security Operations Center
                  </div>
                </div>
                
                <div className="d-flex gap-4 align-items-center">
                 <div style={{ 
                                                background: stats.subscription_status === 'active' 
                                                  ? 'rgba(0, 242, 255, 0.1)' 
                                                  : stats.subscription_status === 'loading' 
                                                    ? 'rgba(148, 163, 184, 0.1)' 
                                                    : 'rgba(239, 68, 68, 0.1)',  
                                                
                                                color: stats.subscription_status === 'active' 
                                                  ? '#00f2ff' 
                                                  : stats.subscription_status === 'loading' 
                                                    ? '#94a3b8' 
                                                    : '#ef4444', 
                                                
                                                padding: '4px 14px', 
                                                borderRadius: '20px', 
                                                fontSize: '0.8rem', 
                                                border: `1px solid ${
                                                  stats.subscription_status === 'active' 
                                                    ? '#00f2ff44' 
                                                    : stats.subscription_status === 'loading' 
                                                      ? '#94a3b844' 
                                                      : '#ef444444'
                                                }`, 
                                                fontWeight: '600' 
                                              }}>
                                                {stats.subscription_status === 'loading' 
                                                  ? 'Verifying license...' 
                                                  : stats.subscription_status === 'active'
                                                    ? `Plan: ${stats.plan_name} (${stats.agents_online}/${stats.max_servers})`
                                                    : 'SUBSCRIPTION INACTIVE'
                                                }
                                              </div>
                                              <div className="header-metric small text-secondary">
                                            <BrainCircuit size={14} className="me-1" style={{ color: '#a855f7' }}/> AI Tokens: <b className={stats.ai_tokens_spent >= stats.ai_tokens_limit ? "text-danger" : "text-white"}>
                                                {stats.ai_tokens_spent?.toLocaleString() || 0} / {stats.ai_tokens_limit?.toLocaleString() || '1,000,000'}
                                            </b>
                                          </div>
                  <div style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '4px 14px', borderRadius: '20px', fontSize: '0.8rem', border: '1px solid #10b98144', fontWeight: '600' }}>
                     Core: Healthy
                  </div>
                  <div className="header-metric small text-secondary">
                     <Server size={14} className="me-1" style={{ color: '#00f2ff' }}/> Agents Online: <b className="text-white">{stats.agents_online}</b>
                  </div>
                  <div className="header-metric small text-secondary">
                     <Wifi size={14} className="me-1" style={{ color: '#00f2ff' }}/> Uptime: <b className="text-white" style={{ color: '#00f2ff' }}>{stats.uptime}</b>
                  </div>
                </div>
              </header>

        
          {stats.ai_tokens_spent >= stats.ai_tokens_limit && (
            <div className="alert bg-danger bg-opacity-10 border-danger text-danger d-flex align-items-center gap-3 m-4 py-3 shadow-lg" 
                style={{ borderRadius: '12px', border: '1px solid #ef4444' }}>
              <ShieldAlert size={24} />
              <div className="flex-grow-1">
                <div className="fw-bold">ATTENTION! AI TOKENS EXPIRED</div>
                <div className="small opacity-80">
                  Your AI analysis quota is empty. Please 
                  <b className="text-decoration-underline ms-1 pointer" style={{cursor: 'pointer'}} onClick={() => navigate('/subscription')}>upgrade your plan</b> 
                  to continue using AI-NOC intelligent features.
                </div>
              </div>
              <button className="btn btn-danger btn-sm fw-bold px-3" onClick={() => navigate('/subscription')}>
                UPGRADE NOW
              </button>
            </div>
          )}


        
        <div className="content-body p-4"> 
            {isLimitReached && (
              <div className="alert border-warning bg-warning bg-opacity-10 text-warning d-flex align-items-center gap-3 mb-4 shadow-sm" style={{ borderRadius: '12px' }}>
                <AlertTriangle size={24} />
                <div>
                  <div className="fw-bold">Subscription Limit Reached</div>
                  <div className="small">Your current plan allows up to {stats.max_servers} servers. Please upgrade your subscription to add new agents.</div>
                </div>
                <button onClick={() => navigate('/subscription')} className="btn btn-warning btn-sm ms-auto fw-bold px-4">UPGRADE NOW</button>
              </div>
            )}

          
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold m-0">Agent Deployment</h2>
                    <p className="text-secondary small m-0">Automated secure installation for remote nodes</p>
                </div>
                <button onClick={() => navigate('/infrastructure')} className="btn btn-outline-secondary btn-sm fw-bold px-4 d-flex align-items-center gap-2" style={{ border: '1px solid #334155', color: '#94a3b8' }}>
                    <ArrowLeft size={14} /> Back to Infrastructure
                </button>
            </div>

            
            <div className={`card bg-dark border-info mb-5 ${isLimitReached ? 'opacity-50' : ''}`} style={{ background: 'rgba(0, 242, 255, 0.03)', transition: '0.3s' }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center gap-2 mb-3 text-info fw-bold"><UserPlus size={20} /> Node Identification</div>
                {isLimitReached ? (
                  <div className="text-danger small fw-bold p-2 bg-danger bg-opacity-10 rounded">Deployment of new agents is locked. Please upgrade your plan to continue.</div>
                ) : (
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <label className="text-secondary small mb-2 uppercase fw-bold">Target Hostname</label>
                      <input type="text" className="form-control bg-black border-secondary text-info fw-bold" placeholder="e.g. pve-02" value={hostname} onChange={(e) => setHostname(e.target.value)} disabled={isLimitReached}/>
                    </div>
                    <div className="col-md-6">
                      <label className="text-secondary small mb-2 uppercase fw-bold">Generated Agent ID</label>
                      <div className="form-control bg-dark border-0 text-secondary fw-bold" style={{ background: '#161b22' }}>{agentId}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            
            {!isLimitReached && hostname.trim() ? (
              <div className="steps-container">
                
                <div className="mb-5">
                  <h5 className="fw-bold mb-3"><span className="badge bg-info text-dark rounded-circle me-2">1</span> Environment Check</h5>
                  <div className="ps-4">
                    <div className="bg-black p-2 rounded border border-secondary mb-3 d-flex justify-content-between align-items-center">
                      <code className="text-warning">{version === 'py3' ? 'python3 --version' : 'python --version'}</code>
                      <button className="btn btn-link p-0 text-secondary" onClick={() => handleCopy(version === 'py3' ? 'python3 --version' : 'python --version', 's1')}>
                        {copiedId === 's1' ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                      </button>
                    </div>
                    <div className="d-flex gap-3">
                      <button onClick={() => setVersion('py3')} className={`btn btn-sm px-4 fw-bold ${version === 'py3' ? 'btn-info' : 'btn-outline-secondary'}`}>Python 3.x</button>
                      <button onClick={() => setVersion('py2')} className={`btn btn-sm px-4 fw-bold ${version === 'py2' ? 'btn-info' : 'btn-outline-secondary'}`}>Python 2.7</button>
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <h5 className="fw-bold mb-3"><span className="badge bg-info text-dark rounded-circle me-2">2</span> Download & Configure</h5>
                  <div className="ps-4">
                    <div className="bg-black p-3 rounded border border-secondary position-relative">
                      <button className="position-absolute text-secondary btn btn-link p-0" style={{ top: '10px', right: '10px' }} onClick={() => handleCopy(step2Code, 's2')}>
                        {copiedId === 's2' ? <Check size={18} className="text-success" /> : <Copy size={18} />}
                      </button>
                      <pre className="m-0" style={{ color: '#00f2ff', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                        {step2Code}
                      </pre>
                    </div>
                  </div>
                </div>

               
                <div className="mb-5">
                  <h5 className="fw-bold mb-3"><span className="badge bg-info text-dark rounded-circle me-2">3</span> Start Monitoring</h5>
                  <div className="ps-4">
                    <div className="bg-black p-3 rounded border border-secondary mb-3 d-flex justify-content-between align-items-center">
                      <code style={{ color: '#10b981' }}>{startCommand}</code>
                      <button className="btn btn-link p-0 text-secondary" onClick={() => handleCopy(startCommand, 's3')}>
                        {copiedId === 's3' ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                      </button>
                    </div>
                    <p className="text-secondary small mb-2">Verify the process is running:</p>
                    <div className="bg-black p-2 rounded border border-secondary mb-3 d-flex justify-content-between align-items-center">
                      <code className="text-light">ps aux | grep agent.py</code>
                      <button className="btn btn-link p-0 text-secondary" onClick={() => handleCopy('ps aux | grep agent.py', 's4')}>
                        {copiedId === 's4' ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

               
                <div className="card border-success bg-dark mb-5" style={{ background: 'rgba(16, 185, 129, 0.02)' }}>
                  <div className="card-body p-4">
                    <h6 className="text-success fw-bold d-flex align-items-center gap-2 mb-3">
                      <ShieldCheck size={20} /> Automatic Enrollment Active
                    </h6>
                    <p className="small text-secondary m-0">No manual approval required. Once the script is started, the node will be authenticated via its secure token and will appear on your dashboard automatically.</p>
                  </div>
                </div>

              </div>
            ) : (
                <div className="text-center p-5 mt-5">
                    <Wifi size={48} className="text-secondary opacity-20 mb-3" />
                    <p className="text-secondary">
                        {isLimitReached 
                          ? "Upgrade your subscription to install more agents." 
                          : "Enter a target hostname above to generate installation steps."
                        }
                    </p>
                </div>
            )}
        </div>
      </div>
      </SubscriptionGuard>
    </div>
  );
};

export default InstallPage;